import React, { useEffect, useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui';
import { Modal, ModalContent, ModalFooter, ModalHeader, ModalTitle } from '../../../components/ui/Modal';
import {
  CREW_FILE_PRINT_SECTIONS,
  countSelectedCrewFilePrintSections,
  defaultCrewFilePrintSections,
  type CrewFilePrintSectionKey,
  type CrewFilePrintSectionSelection,
} from './crewFilePrintSections';

export type CrewFilePrintSectionsModalProps = {
  open: boolean;
  value: CrewFilePrintSectionSelection;
  onClose: () => void;
  /** Apply selection and continue (preview / print). */
  onConfirm: (next: CrewFilePrintSectionSelection) => void;
  title?: string;
  confirmLabel?: string;
};

export const CrewFilePrintSectionsModal: React.FC<CrewFilePrintSectionsModalProps> = ({
  open,
  value,
  onClose,
  onConfirm,
  title = 'Print crew file',
  confirmLabel = 'Print',
}) => {
  const { colors } = useTheme();
  const [draft, setDraft] = useState<CrewFilePrintSectionSelection>(value);

  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  const selectedCount = countSelectedCrewFilePrintSections(draft);
  const allOn = selectedCount === CREW_FILE_PRINT_SECTIONS.length;
  const noneOn = selectedCount === 0;

  const toggle = (key: CrewFilePrintSectionKey, checked: boolean) => {
    setDraft((prev) => ({ ...prev, [key]: checked }));
  };

  return (
    <Modal isOpen={open} onClose={onClose} size="md">
      <ModalHeader>
        <ModalTitle>{title}</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <p className={cn('mb-3 text-sm', colors.textMuted)}>
          Choose which sections to include in the printed crew file.
        </p>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setDraft(defaultCrewFilePrintSections(true))}
            disabled={allOn}
          >
            Select all
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setDraft(defaultCrewFilePrintSections(false))}
            disabled={noneOn}
          >
            Clear
          </Button>
          <span className={cn('ml-auto text-xs', colors.textMuted)}>
            {selectedCount} of {CREW_FILE_PRINT_SECTIONS.length} selected
          </span>
        </div>
        <div
          className={cn(
            'max-h-[min(24rem,50vh)] space-y-0.5 overflow-y-auto rounded-lg border p-1',
            colors.border,
          )}
        >
          {CREW_FILE_PRINT_SECTIONS.map((section) => (
            <label
              key={section.key}
              className={cn(
                'flex cursor-pointer select-none items-center gap-3 rounded-md px-3 py-2',
                colors.interactiveHover,
              )}
            >
              <input
                type="checkbox"
                checked={draft[section.key]}
                onChange={(e) => toggle(section.key, e.target.checked)}
                className="rounded border-gray-300"
              />
              <span className={cn('text-sm', colors.text)}>{section.label}</span>
            </label>
          ))}
        </div>
      </ModalContent>
      <ModalFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="accent"
          disabled={noneOn}
          onClick={() => onConfirm(draft)}
        >
          {confirmLabel}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
